import _ from "lodash";
import { call, put, takeLatest } from "redux-saga/effects";
import {
  POST_AuthAdminLogin,
  PUT_Password,
  POST_ForgotPassword,
  POST_ResetPassword,
} from "../services/Auth";
import exception from "../utils/exception";
import { jwtDecode, jwtEncode } from "../utils/jwt";
import { setToken, removeToken, getToken } from "../utils/token";

function* POST_AuthAdminLoginEffect({ payload, callback, loading, onError }) {
  if (loading) loading(true);
  try {
    const formData = new FormData();
    const data = yield call(jwtEncode, payload);
    formData.append("data", data);

    const res = yield call(POST_AuthAdminLogin, formData);
    const resData = yield call(jwtDecode, res.data);
    const token = _.get(resData, "data.access_token", "");
    const account = _.get(resData, "data.account", "");
    const name = _.get(resData, "data.name", "");
    yield call(setToken, token);

    // 記住帳號和姓名
    if (account) localStorage.setItem("LAST_ACCOUNT", account);
    if (name) localStorage.setItem("LAST_NAME", name);

    yield put({ type: "INITIAL", callback });
  } catch (error) {
    // 登入失敗要在卡片內顯示紅字而非 alert；且帳密錯誤會回 401，
    // 交給 exception 會直接導回 /Auth/Login 把錯誤訊息沖掉，故讓呼叫端可接管。
    if (onError) {
      const msg = _.get(error, "response.data.res_msg", "");
      yield call(onError, msg || "帳號或密碼錯誤，請重新確認");
    } else {
      yield call(exception, error);
    }
  } finally {
    if (loading) loading(false);
  }
}

function* PUT_PasswordEffect({ payload, callback, loading, onError }) {
  if (loading) loading(true);
  try {
    const token = yield call(getToken);
    const formData = new FormData();
    const data = yield call(jwtEncode, payload);
    formData.append("data", data);
    yield call(PUT_Password, token, formData);
    if (callback) callback();
  } catch (error) {
    // 「目前密碼不正確」「新密碼至少 8 個字」這類都要顯示在對話框裡，
    // 交給 exception 會跳 alert 又可能把使用者踢回登入頁，所以讓呼叫端接管。
    if (onError) {
      const msg = _.get(error, "response.data.res_msg", "");
      yield call(onError, (msg || "更新失敗").replace(/^更新失敗:\s*/, ""));
    } else {
      yield call(exception, error);
    }
  } finally {
    if (loading) loading(false);
  }
}

/**
 * 忘記密碼 / 重設密碼共用的 effect 產生器。
 * 兩支都是匿名端點、都要把後端訊息顯示在卡片內（不能跳 alert 或導回登入頁），
 * 流程一模一樣，差別只在打哪支 API 與錯誤前綴。
 */
function makeAnonPasswordEffect(api, prefixPattern) {
  return function* effect({ payload, callback, loading, onError }) {
    if (loading) loading(true);
    try {
      const formData = new FormData();
      const data = yield call(jwtEncode, payload);
      formData.append("data", data);
      yield call(api, formData);
      if (callback) callback();
    } catch (error) {
      const msg = _.get(error, "response.data.res_msg", "");
      const clean = (msg || "操作失敗").replace(prefixPattern, "");
      if (onError) yield call(onError, clean);
      else yield call(exception, error);
    } finally {
      if (loading) loading(false);
    }
  };
}

const POST_ForgotPasswordEffect = makeAnonPasswordEffect(POST_ForgotPassword, /^寄送失敗:\s*/);
const POST_ResetPasswordEffect = makeAnonPasswordEffect(POST_ResetPassword, /^重設失敗:\s*/);

function* Logout({ callback, loading }) {
  if (loading) loading(true);
  try {
    yield call(removeToken);
    yield put({ type: "CLEAR" });
    if (callback) callback(false);
  } catch (error) {
    yield call(exception, error);
  } finally {
    if (loading) loading(false);
  }
}

export default function* Example() {
  yield takeLatest("POST_AuthAdminLogin", POST_AuthAdminLoginEffect);
  yield takeLatest("PUT_Password", PUT_PasswordEffect);
  yield takeLatest("POST_ForgotPassword", POST_ForgotPasswordEffect);
  yield takeLatest("POST_ResetPassword", POST_ResetPasswordEffect);
  yield takeLatest("Logout", Logout);
}
